export type Order = "asc" | "desc";

export type Comparator<T> = (a: T, B: T) => number;

/**
 * This function is a comparator to compare elements and return a number as result
 * @param a A Object
 * @param b Another Object compared with a
 * @param orderBy The key for comparing
 */
const descendingComparator = <Obj, Key extends keyof Obj>(
	a: Obj,
	b: Obj,
	orderBy: Key,
): number => {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
};

/**
 * This function is a comparator to compare elements by asc or desc
 * will return a function interface to call descendingComparator(a, b, key)
 * @param order The oder for sort ("asc" or "desc")
 * @param key The key for comparing
 */
export const getComparator = <Obj, Key extends keyof Obj>(
	order: Order,
	key: Key,
): Comparator<Obj> => {
	if (order === "desc") {
		return (a, b) => descendingComparator(a, b, key);
	}
	return (a, b) => -descendingComparator(a, b, key);
};

/**
 * this function is used to sort the table according to comparator's result and create a index
 * @param array The array of object for sorting
 * @param comparator The comparator method with two object input
 */
export const stableSort = <T>(
	array: T[],
	comparator: (a: T, b: T) => number,
): T[] => {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
};
